import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, X, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, imgSrc } from "../../lib/api";
import { AdminShell } from "../../components/AdminShell";

const INPUT_CLASS =
  "w-full border border-white/10 bg-[#0d0d0d] px-4 py-3 text-sm font-light text-[#F5F5F5] placeholder:text-[#5a5a5a] transition-colors duration-300 focus:border-gold focus:outline-none rounded-none";

const KATEGORI = [
  "Hukum Pidana", "Hukum Perdata", "Hukum Keluarga",
  "Pertanahan & Agraria", "Ketenagakerjaan", "Hukum Perusahaan", "Bantuan Hukum",
];

const EMPTY = { judul: "", kategori: KATEGORI[0], penulis: "", gambar: "", ringkasan: "", isi: "" };

export default function AdminArtikel() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(null); // null = tertutup, object = buka
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api
      .get("/articles")
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditId(null); setForm({ ...EMPTY }); };
  const openEdit = (a) => {
    setEditId(a.id);
    setForm({
      judul: a.judul, kategori: a.kategori, penulis: a.penulis,
      gambar: a.gambar, ringkasan: a.ringkasan, isi: a.isi.join("\n\n"),
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let gambar = form.gambar || undefined;
      if (form.file) {
        const fd = new FormData();
        fd.append("file", form.file);
        const up = await api.post("/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        gambar = up.data.url;
      }
      const payload = {
        judul: form.judul,
        kategori: form.kategori,
        penulis: form.penulis || undefined,
        gambar,
        ringkasan: form.ringkasan,
        isi: form.isi.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean),
      };
      if (editId) {
        await api.put(`/articles/${editId}`, payload);
        toast.success("Artikel diperbarui.");
      } else {
        await api.post("/articles", payload);
        toast.success("Artikel baru diterbitkan.");
      }
      setForm(null);
      load();
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("ylbh_admin_token");
        navigate("/admin/login");
      } else {
        toast.error(formatApiError(err.response?.data?.detail) || "Gagal menyimpan artikel.");
      }
    } finally {
      setSaving(false);
    }
  };

  const hapus = async (a) => {
    if (!window.confirm(`Hapus artikel "${a.judul}"? Tindakan ini tidak bisa dibatalkan.`)) return;
    try {
      await api.delete(`/articles/${a.id}`);
      toast.success("Artikel dihapus.");
      load();
    } catch {
      toast.error("Gagal menghapus artikel.");
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <AdminShell title="Kelola Artikel">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-serif text-4xl text-[#F5F5F5]">Artikel</h1>
        <button
          onClick={openCreate}
          data-testid="artikel-baru-button"
          className="flex items-center gap-2 bg-gold px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.2em] text-ink transition-all hover:bg-gold-light"
        >
          <Plus size={15} /> Tulis Artikel
        </button>
      </div>

      {form && (
        <form
          data-testid="artikel-form"
          onSubmit={submit}
          className="mt-8 space-y-5 border border-gold/30 bg-[#0A0A0A] p-6 lg:p-8"
        >
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-2xl text-[#F5F5F5]">
              {editId ? "Ubah Artikel" : "Artikel Baru"}
            </h2>
            <button type="button" onClick={() => setForm(null)} aria-label="Tutup formulir" data-testid="artikel-form-close">
              <X size={20} className="text-[#737373] hover:text-white" />
            </button>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-2 block text-[11px] font-medium uppercase tracking-[0.3em] text-[#A3A3A3]">Judul *</label>
              <input data-testid="artikel-judul-input" required minLength={5} value={form.judul} onChange={set("judul")} className={INPUT_CLASS} placeholder="Judul artikel" />
            </div>
            <div>
              <label className="mb-2 block text-[11px] font-medium uppercase tracking-[0.3em] text-[#A3A3A3]">Kategori *</label>
              <select data-testid="artikel-kategori-select" value={form.kategori} onChange={set("kategori")} className={`${INPUT_CLASS} appearance-none`}>
                {KATEGORI.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-2 block text-[11px] font-medium uppercase tracking-[0.3em] text-[#A3A3A3]">Penulis</label>
              <input data-testid="artikel-penulis-input" value={form.penulis} onChange={set("penulis")} className={INPUT_CLASS} placeholder="Tim YLBH Sumatera Timur" />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-2 block text-[11px] font-medium uppercase tracking-[0.3em] text-[#A3A3A3]">
                Gambar Sampul — unggah dari komputer (JPG/PNG/WebP, maks 5 MB)
              </label>
              <div className="flex flex-wrap items-center gap-5">
                <label
                  data-testid="artikel-upload-label"
                  className="flex cursor-pointer items-center gap-2 border border-dashed border-gold/50 px-5 py-3 text-[12px] font-semibold uppercase tracking-[0.2em] text-gold transition-colors hover:bg-gold hover:text-ink"
                >
                  <ImagePlus size={15} /> Pilih Gambar
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    data-testid="artikel-file-input"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) setForm((prev) => ({ ...prev, file: f, preview: URL.createObjectURL(f) }));
                    }}
                  />
                </label>
                {(form.preview || form.gambar) && (
                  <img
                    src={form.preview || imgSrc(form.gambar)}
                    alt="Pratinjau gambar artikel"
                    data-testid="artikel-preview-image"
                    className="h-20 w-32 border border-white/10 object-cover"
                  />
                )}
              </div>
              <input
                data-testid="artikel-gambar-input"
                value={form.gambar}
                onChange={set("gambar")}
                className={`${INPUT_CLASS} mt-3`}
                placeholder="…atau tempel URL gambar di sini (opsional)"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-2 block text-[11px] font-medium uppercase tracking-[0.3em] text-[#A3A3A3]">Ringkasan *</label>
              <textarea data-testid="artikel-ringkasan-textarea" required minLength={20} rows={2} value={form.ringkasan} onChange={set("ringkasan")} className={`${INPUT_CLASS} resize-y`} placeholder="Ringkasan singkat yang tampil di kartu artikel" />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-2 block text-[11px] font-medium uppercase tracking-[0.3em] text-[#A3A3A3]">Isi Artikel * — pisahkan paragraf dengan baris kosong</label>
              <textarea data-testid="artikel-isi-textarea" required minLength={50} rows={10} value={form.isi} onChange={set("isi")} className={`${INPUT_CLASS} resize-y`} placeholder="Paragraf pertama…&#10;&#10;Paragraf kedua…" />
            </div>
          </div>
          <button
            type="submit"
            data-testid="artikel-simpan-button"
            disabled={saving}
            className="bg-gold px-8 py-3.5 text-[12px] font-semibold uppercase tracking-[0.2em] text-ink transition-all hover:bg-gold-light disabled:opacity-60"
          >
            {saving ? "Menyimpan…" : editId ? "Simpan Perubahan" : "Terbitkan"}
          </button>
        </form>
      )}

      <div className="mt-10 space-y-3">
        {items === null ? (
          <p className="py-16 text-center font-serif text-xl italic text-[#737373]">Memuat…</p>
        ) : (
          items.map((a) => (
            <div
              key={a.id}
              data-testid={`artikel-row-${a.slug}`}
              className="flex flex-wrap items-center gap-x-5 gap-y-2 border border-white/10 bg-[#0d0d0d] px-5 py-4"
            >
              <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">{a.kategori}</span>
              <span className="font-serif text-lg text-[#F5F5F5]">{a.judul}</span>
              <span className="text-xs text-[#737373]">{a.tanggal}</span>
              <span className="ml-auto flex gap-2">
                <button
                  onClick={() => openEdit(a)}
                  data-testid={`artikel-edit-${a.slug}`}
                  className="flex items-center gap-1.5 border border-white/15 px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.2em] text-[#A3A3A3] transition-colors hover:border-gold hover:text-gold"
                >
                  <Pencil size={12} /> Ubah
                </button>
                <button
                  onClick={() => hapus(a)}
                  data-testid={`artikel-hapus-${a.slug}`}
                  className="flex items-center gap-1.5 border border-red-500/30 px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.2em] text-red-300/80 transition-colors hover:border-red-400 hover:text-red-300"
                >
                  <Trash2 size={12} /> Hapus
                </button>
              </span>
            </div>
          ))
        )}
      </div>
    </AdminShell>
  );
}
